import React, { useState, useEffect, useRef } from "react";
import {
    Fab,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

import AdminTable from "../utlis/AdminTable";
import filesize from "filesize";
import saveAs from "file-saver";
import JSZipUtils from "jszip-utils";

import DownloadButton from "../utlis/DownloadButton";
import axios from "axios";
import { Box } from "@mui/material";

import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import InputTextDialog from "./InputTextDialog";
import ConfirmDialog from "../Popup/ConfirmDialog";
import { useTranslation } from 'react-i18next';

const AttachmentsStep = ({
    showErrorMessage,
    upload,
    remove,
    modified,
    tableRows,
}) => {
    const [loading, setLoading] = useState(false);

    const { uploadedFiles, setUploadedFiles } = upload;
    const { removedFiles, setRemovedFiles } = remove;
    const { setModifiedFiles } = modified;
    const { rows, setRows } = tableRows;
    const [selected, setSelected] = useState([]);
    const [progress, setProgress] = useState({});
    const [showEditFileNameDialog, setShowEditFileNameDialog] = useState(false);
    const [editedFile, setEditedFile] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState({
        open: false,
        mode: null,
        payload: null,
    });
    const [previewDialog, setPreviewDialog] = useState({
        open: false,
        url: "",
        fileName: "",
        filePath: "",
        kind: "unsupported",
        loading: false,
        error: "",
        localFile: null,
    });
    const previewObjectUrlRef = useRef(null);
    const uploadKeyCounterRef = useRef(0);
    const { t } = useTranslation();
    const previewNotAvailableText = t("buttonCommons.previewNotAvailable", {
        defaultValue: "Preview is not available for this file type.",
    });
    const previewLoadErrorText = t("buttonCommons.previewLoadError", {
        defaultValue: "Failed to load preview.",
    });

    const buildUploadRowKey = () => {
        uploadKeyCounterRef.current += 1;
        return `upload-${Date.now()}-${uploadKeyCounterRef.current}`;
    };

    const upsertModifiedFile = (fileId, patch) => {
        if (fileId == null || fileId < 0) {
            return;
        }
        setModifiedFiles((prev) => {
            const next = Array.isArray(prev) ? [...prev] : [];
            const idx = next.findIndex((entry) => entry.Id === fileId);
            if (idx > -1) {
                next[idx] = {
                    ...next[idx],
                    ...patch,
                };
            } else {
                next.push({
                    Id: fileId,
                    ...patch,
                });
            }
            return next;
        });
    };

    const syncOrderMetadataFromRows = (nextRows, nextUploadedFiles = null) => {
        const rowsWithIndex = (nextRows || []).map((row, index) => ({
            ...row,
            Position: index,
        }));

        setRows(rowsWithIndex);

        const existingIds = new Set(
            rowsWithIndex
                .filter((row) => row.Id > -1)
                .map((row) => row.Id)
        );
        setModifiedFiles((prev) => {
            const map = new Map();
            (prev || []).forEach((entry) => {
                if (entry && entry.Id != null && existingIds.has(entry.Id)) {
                    map.set(entry.Id, { ...entry });
                }
            });

            rowsWithIndex.forEach((row, index) => {
                if (row.Id > -1) {
                    const current = map.get(row.Id) || { Id: row.Id };
                    current.Position = index;
                    map.set(row.Id, current);
                }
            });
            return Array.from(map.values());
        });

        setUploadedFiles((prev) => {
            const source = Array.isArray(nextUploadedFiles)
                ? nextUploadedFiles
                : prev || [];
            const uploadOrderByKey = new Map();
            rowsWithIndex.forEach((row, index) => {
                if (row.Id === -1 && row.RowKey) {
                    uploadOrderByKey.set(row.RowKey, index);
                }
            });

            return source
                .map((uploaded) => {
                    const nextPosition = uploadOrderByKey.get(uploaded.rowKey);
                    if (nextPosition == null) {
                        return uploaded;
                    }
                    return {
                        ...uploaded,
                        position: nextPosition,
                    };
                })
                .sort((a, b) => {
                    const posA =
                        typeof a.position === "number"
                            ? a.position
                            : Number.MAX_SAFE_INTEGER;
                    const posB =
                        typeof b.position === "number"
                            ? b.position
                            : Number.MAX_SAFE_INTEGER;
                    return posA - posB;
                });
        });
    };

    const revokePreviewObjectUrl = () => {
        if (previewObjectUrlRef.current) {
            URL.revokeObjectURL(previewObjectUrlRef.current);
            previewObjectUrlRef.current = null;
        }
    };

    const TABLE_HEAD = [
        { id: "FileName", label: t('AttachmentsStep.AdminTable.headers.FileName'), alignRight: false, search: true },
        { id: "FilePath", label: t('AttachmentsStep.AdminTable.headers.FilePath'), alignRight: false, search: false },
        { id: "FileSize", label: t('AttachmentsStep.AdminTable.headers.FileSize'), alignRight: false, search: false },
        { id: "Preview", label: t('AttachmentsStep.AdminTable.headers.Preview'), alignRight: false, search: false },
        { id: "Type",     label: t('AttachmentsStep.AdminTable.headers.Type'), alignRight: false, search: false },
    ];

    const downloadFile = async (filePath, fileName) => {
        saveAs(`${axios.defaults.baseURL}${filePath}`, fileName);
    };

    const resolveFileUrl = (filePath) => {
        if (!filePath) {
            return "";
        }
        if (filePath.startsWith("http://") || filePath.startsWith("https://")) {
            return filePath;
        }
        return `${axios.defaults.baseURL}${filePath}`;
    };

    const resolvePreviewKind = (row) => {
        const fileName = (row.FileName || "").toLowerCase();
        const ext = fileName.includes(".")
            ? fileName.split(".").pop()
            : "";
        const type = (row.Type || row.File?.type || "").toLowerCase();
        const imageExts = ["png", "jpg", "jpeg", "gif", "webp", "svg", "bmp", "avif"];
        const videoExts = ["mp4", "webm", "ogg", "mov", "m4v"];

        if (imageExts.includes(ext) || type === "image" || type.startsWith("image/")) {
            return "image";
        }
        if (ext === "pdf" || type === "application/pdf") {
            return "pdf";
        }
        if (videoExts.includes(ext) || type === "video" || type.startsWith("video/")) {
            return "video";
        }
        return "unsupported";
    };

    const openPreviewDialog = async (row) => {
        revokePreviewObjectUrl();

        const kind = resolvePreviewKind(row);
        const baseState = {
            open: true,
            url: "",
            fileName: row.FileName || "",
            filePath: row.FilePath || "",
            kind,
            loading: false,
            error: "",
            localFile: row.File instanceof File ? row.File : null,
        };

        if (row.File instanceof File) {
            const objectUrl = URL.createObjectURL(row.File);
            previewObjectUrlRef.current = objectUrl;
            setPreviewDialog({
                ...baseState,
                url: objectUrl,
            });
            return;
        }

        const fileUrl = resolveFileUrl(row.FilePath);
        if (!fileUrl) {
            return;
        }

        if (kind === "unsupported") {
            setPreviewDialog(baseState);
            return;
        }

        setPreviewDialog({
            ...baseState,
            loading: true,
        });

        try {
            const response = await axios.get(fileUrl, { responseType: "blob" });
            const objectUrl = URL.createObjectURL(response.data);
            previewObjectUrlRef.current = objectUrl;
            setPreviewDialog((prev) => ({
                ...prev,
                url: objectUrl,
                loading: false,
                error: "",
            }));
        } catch (err) {
            setPreviewDialog((prev) => ({
                ...prev,
                loading: false,
                error: previewLoadErrorText,
            }));
        }
    };

    const closePreviewDialog = () => {
        revokePreviewObjectUrl();
        setPreviewDialog({
            open: false,
            url: "",
            fileName: "",
            filePath: "",
            kind: "unsupported",
            loading: false,
            error: "",
            localFile: null,
        });
    };

    useEffect(() => {
        return () => {
            revokePreviewObjectUrl();
        };
    }, []);

    const renderPreviewContent = () => {
        if (previewDialog.loading) {
            return (
                <Typography variant="body2">
                    {t("buttonCommons.loading", { defaultValue: "Loading..." })}
                </Typography>
            );
        }
        if (previewDialog.error) {
            return (
                <Typography variant="body2">
                    {previewDialog.error}
                </Typography>
            );
        }
        if (!previewDialog.url && previewDialog.kind !== "unsupported") {
            return null;
        }
        if (previewDialog.kind === "unsupported") {
            return (
                <Typography variant="body2">
                    {previewNotAvailableText}
                </Typography>
            );
        }
        if (previewDialog.kind === "image") {
            return (
                <Box
                    component="img"
                    src={previewDialog.url}
                    alt={previewDialog.fileName}
                    sx={{
                        width: "100%",
                        maxHeight: "70vh",
                        objectFit: "contain",
                    }}
                />
            );
        }
        if (previewDialog.kind === "pdf") {
            return (
                <Box
                    component="iframe"
                    src={previewDialog.url}
                    title={previewDialog.fileName}
                    sx={{
                        width: "100%",
                        height: "70vh",
                        border: 0,
                    }}
                />
            );
        }
        if (previewDialog.kind === "video") {
            return (
                <Box
                    component="video"
                    src={previewDialog.url}
                    controls
                    sx={{
                        width: "100%",
                        maxHeight: "70vh",
                    }}
                />
            );
        }
        return null;
    };

    function urlToPromise(url) {
        return new Promise(function (resolve, reject) {
            JSZipUtils.getBinaryContent(url, function (err, data) {
                if (err) {
                    reject(err);
                } else {
                    resolve(data);
                }
            });
        });
    }

    const onDownloadClicked = async (downloadOption) => {
        const filesToDownload = [];
        for (const file of rows) {
            const { FilePath, FileName } = file;
            const isItemSelected = selected.indexOf(FileName) !== -1;
            if (downloadOption === "all" || isItemSelected) {
                filesToDownload.push({
                    fileName: FileName,
                    filePath: FilePath,
                });
            }
        }
        if (filesToDownload.length === 1) {
            downloadFile(
                filesToDownload[0].filePath,
                filesToDownload[0].fileName
            );
        } else {
            const zip = require("jszip")();

            const zipFilename = `${t('AttachmentsStep.attachments')}_${new Date().toISOString()}.zip`;
            for (const file of filesToDownload) {
                const url = `${axios.defaults.baseURL}${file.filePath}`;
                const filename = file.fileName;
                zip.file(filename, urlToPromise(url), { binary: true });
            }

            setProgress({
                show: true,
                value: 0,
            });
            zip.generateAsync(
                { type: "blob" },
                function updateCallback(metadata) {
                    setProgress({
                        show: true,
                        value: metadata.percent.toFixed(2),
                    });
                }
            ).then(
                function callback(blob) {
                    saveAs(blob, zipFilename);
                    setProgress({
                        show: false,
                        value: 0,
                    });
                },
                function (e) {
                    console.log(e);
                }
            );
        }
    };

    const onRemovelicked = (removeOption) => {
        const filesToRemove = new Set(removedFiles);
        const removedUploadRowKeys = new Set();
        const tempRows = rows.filter((file) => {
            const isItemSelected = selected.indexOf(file.FileName) !== -1;
            if (removeOption !== "all" && !isItemSelected) {
                return true;
            }
            if (file.Id !== -1) {
                filesToRemove.add(file.Id);
            } else if (file.RowKey) {
                removedUploadRowKeys.add(file.RowKey);
            }
            return false;
        });

        if (removedUploadRowKeys.size > 0) {
            setUploadedFiles((prev) =>
                (prev || []).filter(
                    (uploaded) => !removedUploadRowKeys.has(uploaded.rowKey)
                )
            );
        }

        setSelected((prevSelected) =>
            prevSelected.filter((name) =>
                tempRows.some((row) => row.FileName === name)
            )
        );
        setRemovedFiles(Array.from(filesToRemove));
        syncOrderMetadataFromRows(tempRows);
    };

    const openDeleteConfirm = (mode, payload) => {
        setDeleteConfirm({
            open: true,
            mode,
            payload,
        });
    };

    const closeDeleteConfirm = () => {
        setDeleteConfirm({
            open: false,
            mode: null,
            payload: null,
        });
    };

    const submitDeleteConfirm = () => {
        if (deleteConfirm.mode === "single-file") {
            deleteFile(deleteConfirm.payload);
        } else if (deleteConfirm.mode === "bulk") {
            onRemovelicked(deleteConfirm.payload);
        }
        closeDeleteConfirm();
    };

    const fileOptions =
        selected.length > 0
            ? [
                  {
                      name: "all",
                      label: t('AdminTableCommons.contextMenuOptions.downloadAll'),
                      handler: onDownloadClicked,
                  },
                  {
                      name: "selected",
                      label: t('AdminTableCommons.contextMenuOptions.downloadSelected'),
                      handler: onDownloadClicked,
                  },
                  {
                      name: "selected",
                      label: t('AdminTableCommons.contextMenuOptions.removeSelected'),
                      handler: (option) => openDeleteConfirm("bulk", option),
                  },
                  {
                      name: "removeAll",
                      label: t('AdminTableCommons.contextMenuOptions.removeAll'),
                      handler: (option) => openDeleteConfirm("bulk", option),
                  },
                  {
                      name: "clear",
                      label: t('AdminTableCommons.contextMenuOptions.clearSelection'),
                      handler: () => {
                          setSelected([]);
                      },
                  },
              ]
            : [
                  {
                      name: "all",
                      label: t('AdminTableCommons.contextMenuOptions.downloadAll'),
                      handler: onDownloadClicked,
                  },
                  {
                      name: "removeAll",
                      label: t('AdminTableCommons.contextMenuOptions.removeAll'),
                      handler: (option) => openDeleteConfirm("bulk", option),
                  },
              ];

    const uploadFile = async (e) => {
        const files = Array.from(e.target.files); // Allow multiple files
        const toBase64 = (file) =>
            new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onload = () => resolve(reader.result);
                reader.onerror = (error) => reject(error);
            });

        setLoading(true);

        try {
            const uploaded = [...uploadedFiles];
            const newRows = [];
            for (const file of files) {
                const name = file.name;
                const size = file.size;
                const type = file.type;
                const base64File = await toBase64(file);
                const rowKey = buildUploadRowKey();

                uploaded.push({
                    name: name,
                    size: size,
                    type: type,
                    file: base64File,
                    rowKey: rowKey,
                });

                newRows.push({
                    Id: -1,
                    RowKey: rowKey,
                    FileName: name,
                    FilePath: "",
                    FileSize: filesize(size),
                    Type: type,
                    File: file,
                });
            }

            syncOrderMetadataFromRows([...rows, ...newRows], uploaded);
        } catch (err) {
            showErrorMessage(err);
        } finally {
            setLoading(false);
            e.target.value = "";
        }
    };

    const editFileName = (id) => {
        setEditedFile(rows[id]);
        setShowEditFileNameDialog(true);
    };

    const deleteFile = (index) => {
        const file = rows[index];
        if (!file) {
            return;
        }
        if (file.Id !== -1) {
            setRemovedFiles((prev) =>
                prev.includes(file.Id) ? prev : [...prev, file.Id]
            );
        } else if (file.RowKey) {
            setUploadedFiles((prev) =>
                (prev || []).filter((uploaded) => uploaded.rowKey !== file.RowKey)
            );
        }
        const tempRows = rows.filter((_, rowIndex) => rowIndex !== index);
        setSelected((prevSelected) =>
            prevSelected.filter((name) =>
                tempRows.some((row) => row.FileName === name)
            )
        );
        syncOrderMetadataFromRows(tempRows);
    };

    const handleFileNameChanged = (value) => {
        if (!editedFile) {
            return;
        }
        const rowIdx = rows.findIndex((row) => row.RowKey === editedFile.RowKey);
        if (rowIdx < 0) {
            setEditedFile(null);
            return;
        }

        const tempRows = [...rows];
        tempRows[rowIdx] = {
            ...tempRows[rowIdx],
            FileName: value,
        };
        setRows(tempRows);

        if (editedFile.Id > -1) {
            upsertModifiedFile(editedFile.Id, { FileName: value });
        } else if (editedFile.RowKey) {
            setUploadedFiles((prev) =>
                (prev || []).map((uploaded) =>
                    uploaded.rowKey === editedFile.RowKey
                        ? {
                              ...uploaded,
                              name: value,
                          }
                        : uploaded
                )
            );
        }

        setEditedFile(null);
        setShowEditFileNameDialog(false);
    };

    const handleRowsReorder = (reorderedRows) => {
        if (!Array.isArray(reorderedRows) || reorderedRows.length !== rows.length) {
            return;
        }
        syncOrderMetadataFromRows(reorderedRows);
    };

    return (
        <>
            <InputTextDialog
                title={t('AttachmentsStep.InputTextDialog.title')}
                initialValue={editedFile ? editedFile.FileName : ""}
                open={showEditFileNameDialog}
                onClose={() => {
                    setShowEditFileNameDialog(false);
                    setEditedFile(null);
                }}
                onSubmit={handleFileNameChanged}
                validationList={rows
                    .filter((row) => {
                        if (editedFile) {
                            return editedFile.FileName !== row.FileName;
                        }
                        return true;
                    })
                    .map((row) => row.FileName)}
                errorText={{
                    empty: t('AttachmentsStep.InputTextDialog.errorText.empty'),
                    alreadyExists: t('AttachmentsStep.InputTextDialog.errorText.alreadyExists')
                }}
            />
            <ConfirmDialog
                open={deleteConfirm.open}
                onClose={closeDeleteConfirm}
                onSubmit={submitDeleteConfirm}
                title={t("ConfirmDialog.deleteTitle", {
                    defaultValue: "Confirm deletion",
                })}
                text={t("ConfirmDialog.deleteAttachmentText", {
                    defaultValue:
                        "Do you really want to remove selected attachment(s)? This action cannot be undone.",
                })}
                confirmLabel={t("buttonCommons.delete")}
                confirmColor="error"
            />
            <Dialog
                open={previewDialog.open}
                onClose={closePreviewDialog}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle>{previewDialog.fileName || t("buttonCommons.preview")}</DialogTitle>
                <DialogContent dividers>
                    {renderPreviewContent()}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closePreviewDialog}>
                        {t("buttonCommons.cancel")}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            if (previewDialog.localFile instanceof File) {
                                saveAs(previewDialog.localFile, previewDialog.fileName);
                            } else if (previewDialog.filePath) {
                                downloadFile(previewDialog.filePath, previewDialog.fileName);
                            }
                        }}
                        disabled={
                            !(previewDialog.localFile instanceof File) &&
                            !previewDialog.filePath
                        }
                    >
                        {t("buttonCommons.download")}
                    </Button>
                </DialogActions>
            </Dialog>
            <Box mt={2} sx={{ height: "100%", width: "100%" }}>
                <AdminTable
                    key={rows.length}
                    searchPlaceholder={t('AdminTableCommons.searchPlaceholder')}
                    headers={TABLE_HEAD}
                    rows={rows.map((row) => {
                        const hasPreview =
                            Boolean(resolveFileUrl(row.FilePath)) ||
                            row.File instanceof File;
                        return {
                            ...row,
                            Preview: (
                                <Button
                                    variant="outlined"
                                    onClick={() => openPreviewDialog(row)}
                                    startIcon={<VisibilityOutlinedIcon />}
                                    disabled={!hasPreview}
                                >
                                    {t('buttonCommons.preview')}
                                </Button>
                            ),
                        };
                    })}
                    selection={{
                        selected: selected,
                        setSelected: setSelected,
                    }}
                    progress={progress}
                    rowDrag={{
                        enabled: true,
                        disableWhenFiltered: true,
                        getRowId: (row, index) => row.RowKey || row.Id || index,
                        onReorder: handleRowsReorder,
                    }}
                    contextMenuOptions={[
                        {
                            label: t('AttachmentsStep.AdminTable.contextMenuOptions.editFileName'),
                            icon: <EditOutlinedIcon />,
                            handler: (id) => {
                                editFileName(id);
                            },
                        },
                        {
                            label: t('AttachmentsStep.AdminTable.contextMenuOptions.deleteFile'),
                            icon: <DeleteOutlineOutlinedIcon />,
                            handler: (id) => {
                                openDeleteConfirm("single-file", id);
                            },
                        },
                    ]}
                >
                    <DownloadButton
                        buttonLabel={t('AttachmentsStep.downloadButton.label')}
                        options={fileOptions}
                    />
                    <label htmlFor="upload-file">
                        <input
                            style={{ display: "none" }}
                            id="upload-file"
                            name="upload-file"
                            type="file"
                            onChange={uploadFile}
                            multiple // Allow multiple file uploads
                        />
                        <Fab
                            color="primary"
                            size="small"
                            component="span"
                            aria-label="add"
                            variant="extended"
                        >
                            <UploadFileIcon />
                            &nbsp;{t('AttachmentsStep.uploadButton.label')}
                        </Fab>
                    </label>
                </AdminTable>
            </Box>
        </>
    );
};

export default AttachmentsStep;
